import {toast} from 'react-toastify';
import {showCommunicationError, showNotFoundToast, showSuccess} from 'helpers/alerts';
import {t} from 'i18next';
import {action, makeObservable, observable} from 'mobx';
import {redirectTo} from 'routing/browserHistory';
import {AppRoutePaths} from 'routing/Routes';
import {appState} from 'store/AppStore';
import {FileDto, GarageCreateUpdateDto, GarageDto} from '../../../../../PRMGApiClient';
import {Api} from '../../../../../PRMGApiClient/ApiClient';

export class GarageState {
  @observable
  item: GarageDto = {
    name: undefined,
    description: undefined,
    carsCount: undefined,
    photo: undefined,
  };

  @observable
  image?: File | FileDto | null;

  constructor() {
    makeObservable(this);
  }

  async addGarage(): Promise<number | void | undefined> {
    return Api.GarageApi.apiGarageAddPost({
      garageCreateUpdateDto: this.item,
    })
      .then((res) => {
        if (res.content) {
          const garageId = Number(res.content);
          if (this.image && garageId) {
            Api.GarageApi.apiGarageAssignOrReplaceGaragePhotoGarageIdPost({
              garageId,
              request: this.image as File,
            })
              .then(() => appState.updateGarageData(garageId))
              .catch(showCommunicationError);
          }
          showSuccess(t('Garage added'));
          redirectTo(AppRoutePaths.garages.url);
        } else {
          toast.error(t('No garage id returned'), {className: 'skip-fade'});
        }
        return res.content;
      })
      .catch(showCommunicationError);
  }

  async updateGarage(): Promise<void> {
    const garageId = this.item.id;
    if (garageId) {
      try {
        await Api.GarageApi.apiGarageUpdatePut({
          garageCreateUpdateDto: this.item,
        });
        try {
          if (!!this.item.photo && this.image === null) {
            await Api.GarageApi.apiGarageRemoveGaragePhotoGarageIdPost({garageId});
          } else if (this.image && !('url' in this.image)) {
            await Api.GarageApi.apiGarageAssignOrReplaceGaragePhotoGarageIdPost({
              garageId,
              request: this.image as File,
            });
          }
          appState.getCommonData();
          showSuccess(t('Garage updated'));
          redirectTo(AppRoutePaths.garages.url);
        } catch (err) {
          showCommunicationError(err);
        }
      } catch (err) {
        showCommunicationError(err);
      }
    }
  }

  async deleteGarage(): Promise<void> {
    if (this.item.id) {
      return Api.GarageApi.apiGarageDeleteIdDelete({
        id: this.item.id,
      })
        .then(() => {
          showSuccess(t('Garage deleted'));
          appState.deleteGarageData(this.item);
          redirectTo(AppRoutePaths.garages.url);
        })
        .catch(showCommunicationError);
    }
  }

  async save(payload: GarageCreateUpdateDto) {
    this.item = {...this.item, ...payload};
    return this.item.id ? this.updateGarage() : this.addGarage();
  }

  loadGarage(garageId: number | undefined) {
    if (!garageId) return;

    Api.GarageApi.apiGarageGetIdGet({
      id: garageId,
    })
      .then((res) => {
        if (res.content) {
          this.item = res.content;
          this.image = res.content.photo ?? null;
        } else {
          showNotFoundToast();
          setTimeout(() => redirectTo(AppRoutePaths.garages.url), 1000);
        }
      })
      .catch(showCommunicationError);
  }

  @action
  removeImage() {
    this.image = null;
  }

  @action
  setImage(param: File | null) {
    this.image = param;
  }
}
